import { useEffect, useState } from 'react'

/**
 * Cookie and third-party consent.
 *
 * The session cookie and the app's own local storage are strictly necessary
 * and never wait on this. The one optional extra is Google Fonts: fetching
 * the typefaces sends Google the visitor's IP address, so the stylesheet is
 * only added once someone has said yes. Until then (or after a no) the
 * Tailwind font stacks fall back to Georgia and the system sans-serif.
 *
 * Anything else optional added later — analytics, embeds — belongs behind
 * the same choice, and in the banner's wording and the privacy policy.
 */

export type ConsentChoice = 'accepted' | 'rejected'

// Kept apart from the plan caches, so signing out doesn't ask again.
const CONSENT_KEY = 'snapshot-money:consent:v1'

const FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600;700&display=swap'
const FONTS_LINK_ID = 'google-fonts'

const listeners = new Set<() => void>()

function readChoice(): ConsentChoice | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY)
    return raw === 'accepted' || raw === 'rejected' ? raw : null
  } catch {
    return null
  }
}

/** Adds or removes the optional third-party resources to match the choice. */
function apply(choice: ConsentChoice | null) {
  const existing = document.getElementById(FONTS_LINK_ID)
  if (choice === 'accepted' && !existing) {
    const link = document.createElement('link')
    link.id = FONTS_LINK_ID
    link.rel = 'stylesheet'
    link.href = FONTS_HREF
    document.head.appendChild(link)
  } else if (choice !== 'accepted' && existing) {
    // Takes effect straight away: the page falls back to the local fonts.
    existing.remove()
  }
}

/** The choice for this visit — also held here in case storage refuses it. */
let current: ConsentChoice | null = null

/** Called once at startup (main.tsx), so a returning visitor's fonts start loading before the app renders. */
export function applyStoredConsent() {
  current = readChoice()
  apply(current)
}

export function setConsent(choice: ConsentChoice | null) {
  try {
    if (choice) localStorage.setItem(CONSENT_KEY, choice)
    else localStorage.removeItem(CONSENT_KEY)
  } catch {
    // Nowhere to remember it — the choice holds for this visit and the banner asks again next time.
  }
  current = choice
  apply(choice)
  listeners.forEach((l) => l())
}

/**
 * The stored choice, or null if the visitor hasn't made one. Where storage is
 * unavailable a choice still holds for the rest of the visit.
 */
export function useConsent(): ConsentChoice | null {
  const [choice, setChoice] = useState(current)
  useEffect(() => {
    const listener = () => setChoice(current)
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])
  return choice
}
